import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  Row as TanstackRow,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Popover from "@mui/material/Popover";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

// --- ErrorBoundary ---
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Table Error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-red-500">
          Something went wrong rendering the table. Please try refreshing.
        </div>
      );
    }
    return this.props.children;
  }
}

// --- Operator options ---
const stringOperators = [
  { label: "Contains", fn: "includesString" },
  { label: "Equals", fn: "equalsString" },
  { label: "Starts with", fn: "startsWith" },
] as const;
const numericOperators = [
  { label: "Equals", fn: "equals" },
  { label: "Greater than", fn: "greaterThan" },
  { label: "Less than", fn: "lessThan" },
] as const;
const allOperators = [...stringOperators, ...numericOperators];

type StringOperator = (typeof stringOperators)[number]["fn"];
type NumericOperator = (typeof numericOperators)[number]["fn"];
type Operator = StringOperator | NumericOperator;

// --- Custom filterFns ---
const filterFns: Record<
  Operator,
  (row: any, columnId: string, filterValue: string, addMeta: any) => boolean
> = {
  includesString: (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    if (value == null) return false;
    return String(value)
      .toLowerCase()
      .includes(String(filterValue).toLowerCase());
  },
  equalsString: (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    if (value == null) return false;
    return String(value).toLowerCase() === String(filterValue).toLowerCase();
  },
  startsWith: (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    if (value == null) return false;
    return String(value)
      .toLowerCase()
      .startsWith(String(filterValue).toLowerCase());
  },
  equals: (row, columnId, filterValue) => {
    const value = Number(row.getValue(columnId));
    const filterNum = Number(filterValue);
    if (
      row.getValue(columnId) == null ||
      Number.isNaN(value) ||
      Number.isNaN(filterNum)
    )
      return false;
    return value === filterNum;
  },
  greaterThan: (row, columnId, filterValue) => {
    const value = Number(row.getValue(columnId));
    const filterNum = Number(filterValue);
    if (
      row.getValue(columnId) == null ||
      Number.isNaN(value) ||
      Number.isNaN(filterNum)
    )
      return false;
    return value > filterNum;
  },
  lessThan: (row, columnId, filterValue) => {
    const value = Number(row.getValue(columnId));
    const filterNum = Number(filterValue);
    if (
      row.getValue(columnId) == null ||
      Number.isNaN(value) ||
      Number.isNaN(filterNum)
    )
      return false;
    return value < filterNum;
  },
};

interface DraggableHeaderCellProps {
  index: number;
  moveColumn: (from: number, to: number) => void;
  children: React.ReactNode;
}
const DraggableHeaderCell: React.FC<DraggableHeaderCellProps> = ({
  index,
  moveColumn,
  children,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: "COLUMN",
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  const [, drop] = useDrop({
    accept: "COLUMN",
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });
  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{
        cursor: "move",
        opacity: isDragging ? 0.5 : 1,
        userSelect: "none",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        padding: "0 8px",
        background: isDragging ? "rgba(0,0,0,0.04)" : undefined,
        transition: "background 0.2s",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
};

export interface RowData {
  id: string | number;
  [key: string]: any;
}

export interface DataTableHandle {
  getExportData: () => { headers: string[]; rows: any[][] };
}

const DataTable = React.forwardRef<DataTableHandle, { rawData: unknown }>(
  ({ rawData }, ref) => {
    // --- Parse Data ---
    const rows: RowData[] = useMemo(() => {
      if (typeof rawData === "string") {
        try {
          const jsonStartIndex = rawData.indexOf("[");
          if (jsonStartIndex === -1) {
            return JSON.parse(rawData).map((row: any, idx: number) => ({
              id: row.id ?? idx,
              ...row,
            }));
          }
          const data = JSON.parse(rawData.slice(jsonStartIndex));
          return data.map((row: any, idx: number) => ({
            id: row.id ?? idx,
            ...row,
          }));
        } catch {
          console.error("Error parsing data");
          return [];
        }
      }
      const data = Array.isArray(rawData) ? rawData : [];
      return data.map((row: any, idx: number) => ({
        id: row.id ?? idx,
        ...row,
      }));
    }, [rawData]);

    const columnKeys = useMemo(() => {
      const keys = new Set<string>();
      rows.forEach((row) =>
        Object.keys(row).forEach((k) => k !== "id" && keys.add(k))
      );
      return Array.from(keys);
    }, [rows]);

    // Refs: title nodes (for focus/aria) and dedicated ANCHORS for popper
    const columnTitleNodes = React.useRef<{
      [key: string]: HTMLSpanElement | null;
    }>({});
    const filterAnchorNodes = React.useRef<{
      [key: string]: HTMLSpanElement | null;
    }>({});
    useEffect(() => {
      columnKeys.forEach((key) => {
        if (!columnTitleNodes.current[key])
          columnTitleNodes.current[key] = null;
        if (!filterAnchorNodes.current[key])
          filterAnchorNodes.current[key] = null;
      });
    }, [columnKeys]);

    // --- State ---
    const [columnOrder, setColumnOrder] = useState<number[]>([]);
    useEffect(() => {
      setColumnOrder(columnKeys.map((_, i) => i));
    }, [columnKeys.join(",")]);

    const moveColumn = useCallback((from: number, to: number) => {
      setColumnOrder((prev) => {
        const arr = [...prev];
        const [removed] = arr.splice(from, 1);
        arr.splice(to, 0, removed);
        return arr;
      });
    }, []);

    const sampleRow = rows[0] || {};
    const colTypes: Record<string, "string" | "number"> = useMemo(
      () =>
        Object.fromEntries(
          columnKeys.map((key) => {
            const val = sampleRow[key];
            if (typeof val === "number") return [key, "number"];
            if (!isNaN(Number(val)) && val !== "" && val !== null)
              return [key, "number"];
            return [key, "string"];
          })
        ),
      [columnKeys, sampleRow]
    );

    const [filterOperators, setFilterOperators] = useState<{
      [col: string]: Operator;
    }>({});
    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 10,
    });
    const [globalFilter, setGlobalFilter] = useState("");
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
      {}
    );
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // Columns menu

    // Filter popper state (key only; anchor comes from ref map)
    const [filterPopover, setFilterPopover] = useState<{
      columnKey: string | null;
    }>({ columnKey: null });

    const [selectedFilterValues, setSelectedFilterValues] = useState<{
      [col: string]: Set<any>;
    }>({});
    const [filterSearch, setFilterSearch] = useState<{ [col: string]: string }>(
      {}
    );

    // Unique values per column
    const uniqueColumnValues = useMemo(() => {
      const result: { [key: string]: any[] } = {};
      columnKeys.forEach((key) => {
        const values = Array.from(new Set(rows.map((row) => row[key])));
        result[key] = values.filter((v) => v != null);
      });
      return result;
    }, [columnKeys, rows]);

    // --- Columns (deps included so filterFn updates) ---
    const columns = useMemo<ColumnDef<RowData, any>[]>(
      () =>
        columnOrder.map((orderIdx, colDisplayIdx) => {
          const key = columnKeys[orderIdx];
          return {
            id: key,
            accessorKey: key,
            header: ({ column }: { column: any }) => (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <DraggableHeaderCell
                  index={colDisplayIdx}
                  moveColumn={moveColumn}
                >
                  {/* Title / sort */}
                  <span
                    ref={(node) => {
                      columnTitleNodes.current[key] = node;
                    }}
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      fontWeight: column.getIsSorted() ? "bold" : "normal",
                      marginRight: 8,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={key}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    {key}
                    {column.getIsSorted() === "asc"
                      ? " ▲"
                      : column.getIsSorted() === "desc"
                      ? " ▼"
                      : ""}
                  </span>

                  {/* Filter trigger */}
                  <span
                    style={{
                      fontSize: "1.1em",
                      marginLeft: 4,
                      cursor: "pointer",
                      color:
                        filterPopover.columnKey === key ? "#1976d2" : "#888",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFilterValues((prev) =>
                        prev[key] === undefined
                          ? { ...prev, [key]: new Set(uniqueColumnValues[key]) }
                          : prev
                      );
                      setFilterPopover({ columnKey: key });
                    }}
                    aria-label={`Filter ${key}`}
                  >
                    🔍
                  </span>
                </DraggableHeaderCell>
              </div>
            ),
            cell: (info: { getValue: () => any }) => info.getValue(),
            filterFn: (
              row: TanstackRow<RowData>,
              columnId: string,
              filterValue: string,
              addMeta: (meta: unknown) => void
            ) => {
              if (
                selectedFilterValues[columnId] &&
                selectedFilterValues[columnId].size > 0
              ) {
                return selectedFilterValues[columnId].has(
                  row.getValue(columnId)
                );
              }
              const operator =
                filterOperators[columnId] ||
                (colTypes[columnId] === "number"
                  ? numericOperators[0].fn
                  : stringOperators[0].fn);
              if (!filterValue) return true;
              const fn = filterFns[operator];
              return fn ? fn(row, columnId, filterValue, addMeta) : true;
            },
            enableColumnFilter: true,
            enableSorting: true,
            size: 180,
            minSize: 80,
            maxSize: 600,
          } as ColumnDef<RowData, any>;
        }),
      [
        columnOrder,
        columnKeys,
        moveColumn,
        selectedFilterValues,
        filterOperators,
        colTypes,
        filterPopover.columnKey, // just for icon highlight
      ]
    );

    const table = useReactTable({
      data: rows,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      columnResizeMode: "onChange",
      defaultColumn: { size: 180, minSize: 80, maxSize: 600 },
      state: { globalFilter, sorting, pagination, columnVisibility },
      onGlobalFilterChange: setGlobalFilter,
      onSortingChange: setSorting,
      onPaginationChange: setPagination,
      onColumnVisibilityChange: setColumnVisibility,
      initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
    });

    // Expose filtered, visible data (ignoring pagination) for export
    useImperativeHandle(
      ref,
      () => ({
        getExportData: () => {
          const visibleColumns = table.getVisibleLeafColumns();
          const headers = visibleColumns.map((c) => c.id);
          // Use filtered rows to include all matches, not just current page
          const filteredRows = table.getFilteredRowModel().rows;
          const dataRows = filteredRows.map((row) =>
            visibleColumns.map((c) => row.getValue(c.id))
          );
          return { headers, rows: dataRows };
        },
      }),
      [table]
    );

    const openVisibilityMenu = (e: React.MouseEvent<HTMLButtonElement>) =>
      setAnchorEl(e.currentTarget);
    const closeVisibilityMenu = () => setAnchorEl(null);

    const activeFilters = useMemo(() => {
      const filterState = table.getState().columnFilters;
      return filterState
        .map((filter) => {
          const col = table.getColumn(filter.id);
          if (!col) return null;
          const operator =
            filterOperators[filter.id] ||
            (colTypes[filter.id] === "number"
              ? numericOperators[0].fn
              : stringOperators[0].fn);
          return {
            id: filter.id,
            label: filter.id,
            operator,
            operatorLabel:
              allOperators.find((o) => o.fn === operator)?.label || operator,
            value: filter.value,
          };
        })
        .filter(Boolean) as {
        id: string;
        label: string;
        operator: Operator;
        operatorLabel: string;
        value: string;
      }[];
    }, [table.getState().columnFilters, filterOperators, colTypes, table]);

    const clearAllFilters = () =>
      table.getAllColumns().forEach((c) => c.setFilterValue(""));
    const removeFilter = (colId: string) =>
      table.getColumn(colId)?.setFilterValue("");

    const badgeStyle = {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 8px",
      marginRight: 6,
      marginBottom: 6,
      background: "#f1f5f9",
      borderRadius: 999,
      fontSize: "0.97em",
      border: "1px solid #d0d7de",
      gap: 4,
    } as const;

    // Checkbox handlers
    const handleFilterCheckbox = (col: string, value: any) => {
      setSelectedFilterValues((prev) => {
        const set = new Set(prev[col] || []);
        if (set.has(value)) set.delete(value);
        else set.add(value);
        setTimeout(() => table.getColumn(col)?.setFilterValue("__multi__"), 0);
        return { ...prev, [col]: set };
      });
    };
    const handleSelectAll = (col: string) => {
      setSelectedFilterValues((prev) => ({
        ...prev,
        [col]: new Set(uniqueColumnValues[col]),
      }));
      table.getColumn(col)?.setFilterValue("__multi__");
    };
    const handleClearAll = (col: string) => {
      setSelectedFilterValues((prev) => ({ ...prev, [col]: new Set<any>() }));
      table.getColumn(col)?.setFilterValue("");
    };
    const handleTextFilter = (col: string, value: string) => {
      setFilterSearch((prev) => ({ ...prev, [col]: value }));
      setSelectedFilterValues((prev) => ({ ...prev, [col]: new Set<any>() }));
      table.getColumn(col)?.setFilterValue(value);
    };

    const closeFilterPopover = () => setFilterPopover({ columnKey: null });

    // --- GUARD ---
    if (rows.length === 0 || columnKeys.length === 0) {
      return (
        <ErrorBoundary>
          <div
            className="flex flex-col h-full items-center justify-center"
            style={{ minHeight: 200 }}
          >
            <div style={{ color: "#888", fontSize: "1.2em", marginTop: 40 }}>
              No data to display.
            </div>
          </div>
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary>
        <div className="flex flex-col h-full">
          <div style={{ marginBottom: 6 }}>
            {activeFilters.length > 0 && (
              <div
                style={{
                  marginBottom: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ fontWeight: 500, marginRight: 10, fontSize: "1em" }}
                >
                  Active Filters:
                </span>
                {activeFilters.map((f) => (
                  <span key={f.id} style={badgeStyle}>
                    <b>{f.label}</b> {f.operatorLabel}{" "}
                    <span style={{ color: "#097" }}>{f.value}</span>
                    <button
                      aria-label="Clear filter"
                      onClick={() => removeFilter(f.id)}
                      style={{
                        marginLeft: 3,
                        color: "#888",
                        background: "none",
                        border: "none",
                        fontWeight: "bold",
                        fontSize: "1em",
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <Button
                  size="small"
                  onClick={clearAllFilters}
                  variant="text"
                  style={{
                    marginLeft: 12,
                    fontSize: "0.96em",
                    textTransform: "none",
                    color: "#e53e3e",
                  }}
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>

          <div className="flex flex-row items-center pr-2">
            <TextField
              label="Global Filter"
              size="small"
              fullWidth
              sx={{ mr: 2 }}
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            <div>
              <Button
                variant="outlined"
                size="small"
                onClick={openVisibilityMenu}
              >
                COLUMNS
              </Button>
              <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={closeVisibilityMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <div style={{ padding: 10, minWidth: 160 }}>
                  {columnKeys.map((col) => (
                    <div
                      key={col}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Checkbox
                        size="small"
                        checked={table.getColumn(col)?.getIsVisible() ?? true}
                        onChange={() =>
                          table.getColumn(col)?.toggleVisibility()
                        }
                      />
                      <span style={{ fontSize: "0.97em" }}>{col}</span>
                    </div>
                  ))}
                </div>
              </Popover>
            </div>
          </div>

          <DndProvider backend={HTML5Backend}>
            <div
              style={{
                overflow: "auto",
                height: "calc(100vh - 280px)",
                width: "100%",
              }}
            >
              <table
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  tableLayout: "fixed",
                }}
              >
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) =>
                        header.isPlaceholder ? null : (
                          <th
                            key={header.id}
                            style={{
                              width: header.getSize(),
                              borderBottom: "2px solid #ccc",
                              position: "relative",
                              background: "#f7fafc",
                              verticalAlign: "top",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {/* Invisible, STABLE anchor right under the title area */}
                            <span
                              ref={(node) => {
                                filterAnchorNodes.current[header.column.id] =
                                  node;
                              }}
                              style={{
                                position: "absolute",
                                left: 8,
                                bottom: -2,
                                width: 0,
                                height: 0,
                                pointerEvents: "none",
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                position: "relative",
                                height: "100%",
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}

                              {/* Filter Popper: anchored to the stable span in this <th> */}
                              {filterPopover.columnKey === header.column.id &&
                                filterAnchorNodes.current[header.column.id] && (
                                  <Popper
                                    open
                                    anchorEl={
                                      filterAnchorNodes.current[
                                        header.column.id
                                      ]
                                    }
                                    placement="bottom-start"
                                    popperOptions={{ strategy: "fixed" }}
                                    modifiers={[
                                      {
                                        name: "offset",
                                        options: { offset: [0, 8] },
                                      },
                                      {
                                        name: "preventOverflow",
                                        options: { padding: 8, altAxis: true },
                                      },
                                      {
                                        name: "flip",
                                        options: {
                                          fallbackPlacements: [
                                            "bottom-end",
                                            "top-start",
                                          ],
                                        },
                                      },
                                      // Keep measured position stable in transformed/scrollable parents
                                      {
                                        name: "computeStyles",
                                        options: { adaptive: false },
                                      },
                                    ]}
                                  >
                                    <ClickAwayListener
                                      onClickAway={closeFilterPopover}
                                    >
                                      <Paper
                                        elevation={3}
                                        sx={{
                                          p: 1.5,
                                          minWidth: 240,
                                          maxHeight: 380,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {/* Operator + text */}
                                        <div
                                          style={{
                                            display: "flex",
                                            marginBottom: 8,
                                            gap: 8,
                                            alignItems: "center",
                                          }}
                                        >
                                          <select
                                            id={`operator-${header.column.id}`}
                                            name={`operator-${header.column.id}`}
                                            value={
                                              filterOperators[
                                                header.column.id
                                              ] ||
                                              (colTypes[header.column.id] ===
                                              "number"
                                                ? numericOperators[0].fn
                                                : stringOperators[0].fn)
                                            }
                                            onChange={(e) => {
                                              setFilterOperators((prev) => ({
                                                ...prev,
                                                [header.column.id]: e.target
                                                  .value as Operator,
                                              }));
                                              setSelectedFilterValues(
                                                (prev) => ({
                                                  ...prev,
                                                  [header.column.id]: new Set(),
                                                })
                                              );
                                            }}
                                            style={{
                                              fontSize: "0.95em",
                                              minWidth: 110,
                                            }}
                                          >
                                            <optgroup label="String Operators">
                                              {stringOperators.map((op) => (
                                                <option
                                                  key={op.fn}
                                                  value={op.fn}
                                                >
                                                  {op.label}
                                                </option>
                                              ))}
                                            </optgroup>
                                            <optgroup label="Numeric Operators">
                                              {numericOperators.map((op) => (
                                                <option
                                                  key={op.fn}
                                                  value={op.fn}
                                                >
                                                  {op.label}
                                                </option>
                                              ))}
                                            </optgroup>
                                          </select>
                                          <input
                                            id={`filter-${header.column.id}`}
                                            name={`filter-${header.column.id}`}
                                            value={
                                              filterSearch[header.column.id] ||
                                              ""
                                            }
                                            onChange={(e) => {
                                              handleTextFilter(
                                                header.column.id,
                                                e.target.value
                                              );
                                              setSelectedFilterValues(
                                                (prev) => ({
                                                  ...prev,
                                                  [header.column.id]: new Set(),
                                                })
                                              );
                                            }}
                                            placeholder="Filter…"
                                            style={{
                                              width: "100%",
                                              fontSize: "0.95em",
                                            }}
                                          />
                                        </div>

                                        {/* Bulk actions */}
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: 12,
                                            marginBottom: 8,
                                          }}
                                        >
                                          <Button
                                            size="small"
                                            onClick={() =>
                                              handleSelectAll(header.column.id)
                                            }
                                            style={{ fontSize: "0.95em" }}
                                          >
                                            SELECT ALL
                                          </Button>
                                          <Button
                                            size="small"
                                            onClick={() =>
                                              handleClearAll(header.column.id)
                                            }
                                            style={{
                                              fontSize: "0.95em",
                                              color: "#e53e3e",
                                            }}
                                          >
                                            CLEAR
                                          </Button>
                                        </div>

                                        {/* Values */}
                                        <div
                                          style={{
                                            maxHeight: 220,
                                            overflowY: "auto",
                                          }}
                                        >
                                          {uniqueColumnValues[header.column.id]
                                            .filter((v) =>
                                              filterSearch[header.column.id]
                                                ? String(v)
                                                    .toLowerCase()
                                                    .includes(
                                                      filterSearch[
                                                        header.column.id
                                                      ].toLowerCase()
                                                    )
                                                : true
                                            )
                                            .map((v) => (
                                              <div
                                                key={String(v)}
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 8,
                                                }}
                                              >
                                                <Checkbox
                                                  size="small"
                                                  checked={
                                                    selectedFilterValues[
                                                      header.column.id
                                                    ]?.has(v) || false
                                                  }
                                                  onChange={() =>
                                                    handleFilterCheckbox(
                                                      header.column.id,
                                                      v
                                                    )
                                                  }
                                                />
                                                <span
                                                  style={{ fontSize: "0.97em" }}
                                                >
                                                  {String(v)}
                                                </span>
                                              </div>
                                            ))}
                                          {uniqueColumnValues[header.column.id]
                                            .length === 0 && (
                                            <div
                                              style={{
                                                color: "#888",
                                                fontSize: "0.97em",
                                              }}
                                            >
                                              No values
                                            </div>
                                          )}
                                        </div>
                                      </Paper>
                                    </ClickAwayListener>
                                  </Popper>
                                )}
                            </div>

                            {/* Column resizer (v8: on header) */}
                            {header.column.getCanResize() && (
                              <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: "absolute",
                                  right: -3,
                                  top: 0,
                                  height: "100%",
                                  width: 6,
                                  cursor: "col-resize",
                                  userSelect: "none",
                                  touchAction: "none",
                                  opacity: header.column.getIsResizing()
                                    ? 0.7
                                    : 0,
                                  transition: "opacity 0.15s",
                                }}
                                onMouseEnter={(e) =>
                                  ((e.currentTarget.style.opacity as any) = 0.7)
                                }
                                onMouseLeave={(e) =>
                                  ((e.currentTarget.style.opacity as any) =
                                    header.column.getIsResizing() ? 0.7 : 0)
                                }
                              />
                            )}
                          </th>
                        )
                      )}
                    </tr>
                  ))}
                </thead>

                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            border: "1px solid #eee",
                            padding: "4px 10px",
                            fontSize: "0.97rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={String(cell.getValue())}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DndProvider>

          <div className="flex flex-row items-center justify-end gap-2 pr-2">
            <Button
              size="small"
              variant="outlined"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              PREV
            </Button>
            <span style={{ alignSelf: "center" }}>
              Page {pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              NEXT
            </Button>
            <TextField
              select
              SelectProps={{ native: true }}
              value={pagination.pageSize}
              onChange={(e) =>
                setPagination((old) => ({
                  ...old,
                  pageSize: Number(e.target.value),
                }))
              }
              style={{ width: 70, marginLeft: 8 }}
              size="small"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}/page
                </option>
              ))}
            </TextField>
          </div>
        </div>
      </ErrorBoundary>
    );
  }
);

export function getTableDataForExport(rawData: unknown): {
  headers: string[];
  rows: any[][];
} {
  let rows: RowData[] = [];
  if (typeof rawData === "string") {
    try {
      const jsonStartIndex = rawData.indexOf("[");
      if (jsonStartIndex === -1) {
        rows = JSON.parse(rawData).map((row: any, idx: number) => ({
          id: row.id ?? idx,
          ...row,
        }));
      } else {
        const data = JSON.parse(rawData.slice(jsonStartIndex));
        rows = data.map((row: any, idx: number) => ({
          id: row.id ?? idx,
          ...row,
        }));
      }
    } catch {
      rows = [];
    }
  } else if (Array.isArray(rawData)) {
    rows = rawData.map((row: any, idx: number) => ({
      id: row.id ?? idx,
      ...row,
    }));
  }
  const columnKeys = Array.from(
    new Set(rows.flatMap((row) => Object.keys(row).filter((k) => k !== "id")))
  );
  const headers = columnKeys;
  const dataRows = rows.map((row) => headers.map((key) => row[key]));
  return { headers, rows: dataRows };
}

export default DataTable;
